import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';

import { DialogActions, DialogAdaptive, DialogContent, DialogTitle } from '../../components/material-ui/Dialog';
import { FormControl } from '../../components/material-ui/FormControl';
import { Input } from '../../components/material-ui/Input';
import { InputLabel } from '../../components/material-ui/InputLabel';
import { ListItemText } from '../../components/material-ui/List';
import { MenuItem } from '../../components/material-ui/Menu';
import { Select } from '../../components/material-ui/Select';
import { TextField } from '../../components/material-ui/TextField';
import { DialogControls } from '../../components/DialogControls/DialogControls';
import { useForm } from '../../components/hooks/useForm/useForm';
import { FieldTypes } from '../../components/hooks/useForm/validate';
import { useHideModal } from '../../actions/modal';
import { pushError } from '../../actions/notifications';
import { changeUser } from '../../actions/tasks';
import { getCurrentTaskSelector } from '../../selectors/screen';
import { getTranslateText } from '../../service/utils';
import { useStyles } from './useStyles';

const fields = {
    userId: { title: 'userId', type: FieldTypes.NUMBER },
    password: { title: 'Password', type: FieldTypes.STRING },
};

export const ChangeUser = React.forwardRef((props, ref) => {
    const hideModal = useHideModal();
    const dispatch = useDispatch();
    const classes = useStyles();
    const { t: translate } = useTranslation();
    const { users, currentUserId } = useSelector((state) => {
        const users = get(state, 'users.list', {});
        const currentUserId = get(state, 'screen.currentUser.id');
        const currentTask = getCurrentTaskSelector(state);
        //TODO Для совместимости. После перевода всех пользователей на версию 4.3.6 вернуть как было:
        //const taskUsers = get(currentTask, 'users', []).map((item) => users[item]);
        const taskUsers = get(currentTask, 'users', []).map((item) => (
            { ...users[item],
                name: users[item].nameRus ? users[item].nameRus : users[item].name,
                nameEng: users[item].nameEng ? users[item].nameEng : users[item].name
            }));

        return { currentUserId, users: taskUsers };
    });

    const handleSuccess = React.useCallback((data) => {
        const result = dispatch(changeUser(data.userId, data.password));
        if (result.isOk) {
            hideModal();
        } else {
            dispatch(
                pushError({
                    header: translate('errors.incorrectPass'),
                    error: translate('errors.incorrectUserPass'),
                })
            );
        }
    }, []);

    const { inputs, handleInputChange, handleSubmit, status } = useForm({
        onSuccess: handleSuccess,
        initialData: {
            userId: currentUserId,
        },
        fields,
    });
    const userId = get(inputs, 'userId', 0);
    const password = get(inputs, 'password');

    const actions = React.useMemo(
        () => [
            { label: translate('cancel'), onClick: hideModal },
            {
                label: translate('ok'),
                color: 'primary',
                type: 'submit',
                disabled: !password,
            },
        ],
        [Boolean(password), translate]
    );
    const handleFlightChange = React.useCallback((event) => {
        handleInputChange(event);
    }, []);

    return (
        <DialogAdaptive open={true} maxWidth="md" fullScreenBreakpoint="sm">
            <DialogTitle className={classes.title}>{translate('modal.changeUser')}</DialogTitle>
            <form ref={ref} onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <FormControl className={classes.formControl}>
                        <InputLabel htmlFor="userId">{translate('modal.user')}</InputLabel>
                        <Select name="userId" value={userId} onChange={handleFlightChange} input={<Input id="userId" />}>
                            {users &&
                                users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        <ListItemText primary={`${getTranslateText('name', user)} (${user.login})`} />
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            id="password"
                            name="password"
                            label={translate('loginPage.password')}
                            value={password}
                            onChange={handleInputChange}
                            type="password"
                            error={Boolean(status.password)}
                            helperText={status.password}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            placeholder={translate('modal.enterPass')}
                            className={classes.field}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </form>
        </DialogAdaptive>
    );
});
